import type { DefaultMenuItem, GridApi, MenuItemDef } from "~/components/grid/AgGrid";
import type { UseGridFileExportReturn } from "~/hooks/useGridFileExport";

import i18n from "~/i18n/i18n";

export const contextMenuDefaultItems: DefaultMenuItem[] = [
	"autoSizeAll",
	"copy",
	"copyWithHeaders",
	"separator",
	"export"
];

export const groupExpandMenuItems = (
	api: Pick<GridApi, "getRowGroupColumns">,
	isTreeData: boolean
): DefaultMenuItem[] =>
	isTreeData || api.getRowGroupColumns().length > 0 ? ["expandAll", "contractAll", "separator"] : [];

type ContextMenuItem = DefaultMenuItem | MenuItemDef;

// AG Grid's built-in export items pass no params, so on a server-side grid they write only the cached rows.
const BUILT_IN_EXPORT_ITEMS: ReadonlySet<ContextMenuItem> = new Set<DefaultMenuItem>([
	"export",
	"csvExport",
	"excelExport"
]);

const DOWNLOAD_ICON = '<span class="material-symbols-rounded" style="font-size:16px">download</span>';
const EXCEL_ICON = '<span class="material-symbols-rounded" style="font-size:16px">table_view</span>';

const buildFullExportMenuItem = (fileExport: UseGridFileExportReturn): MenuItemDef => ({
	disabled: fileExport.isExporting,
	icon: DOWNLOAD_ICON,
	name: i18n.t("common:actions.export"),
	subMenu: [
		{
			action: () => void fileExport.exportAs("csv"),
			icon: DOWNLOAD_ICON,
			name: i18n.t("common:grid.export.downloadCsv")
		},
		{
			action: () => void fileExport.exportAs("xlsx"),
			icon: EXCEL_ICON,
			name: i18n.t("common:grid.export.downloadExcel")
		}
	]
});

const trimSeparators = (items: ContextMenuItem[]): ContextMenuItem[] =>
	items.filter(
		(item, index) => item !== "separator" || (index > 0 && index < items.length - 1 && items[index + 1] !== "separator")
	);

interface RouteExportMenuItemsOptions {
	fileExport?: UseGridFileExportReturn;
	isServerSide: boolean;
}

export const routeExportMenuItems = (
	items: ContextMenuItem[],
	{ fileExport, isServerSide }: RouteExportMenuItemsOptions
): ContextMenuItem[] => {
	if (!isServerSide || !items.some((item) => BUILT_IN_EXPORT_ITEMS.has(item))) return items;

	let hasFullExportItem = false;
	const routed = items.flatMap((item): ContextMenuItem[] => {
		if (!BUILT_IN_EXPORT_ITEMS.has(item)) return [item];
		if (!fileExport || hasFullExportItem) return [];
		hasFullExportItem = true;
		return [buildFullExportMenuItem(fileExport)];
	});
	return trimSeparators(routed);
};
